
import { useState } from "react";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Card } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { PageTitle } from "@/components/ui/PageTitle";
import { Contract } from "@/types/contracts";
import { useQuery } from "@tanstack/react-query";
import { AddContractDialog } from "@/components/staff/contracts/AddContractDialog";
import { ContractTerms } from "@/components/staff/contracts/ContractTerms";
import SignatureCanvas from 'react-signature-canvas';
import { Checkbox } from "@/components/ui/checkbox";
import jsPDF from 'jspdf';
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertCircle, FileSignature, Plus } from "lucide-react";

const EmployeeContract = () => {
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isSignatureDialogOpen, setIsSignatureDialogOpen] = useState(false);
  const [signaturePad, setSignaturePad] = useState<SignatureCanvas | null>(null);
  const [selectedContractId, setSelectedContractId] = useState<string | null>(null);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const { data: contracts, isLoading, refetch } = useQuery({
    queryKey: ['employee-contracts'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error("Vous devez être connecté pour voir vos contrats");
        return [];
      }

      const { data, error } = await supabase
        .from('employee_contracts')
        .select('*')
        .eq('owner_id', user.id);

      if (error) {
        console.error("Erreur lors de la récupération des contrats:", error);
        throw error;
      }

      console.log("Contrats récupérés:", data);
      return data as Contract[];
    }
  });

  const handleSign = async () => {
    if (!signaturePad || !selectedContractId || !termsAccepted) {
      if (!termsAccepted) {
        toast.error("Veuillez lire et approuver les termes du contrat");
      }
      return;
    }

    const signatureData = signaturePad.toDataURL();

    try {
      const { error } = await supabase
        .from('employee_contracts')
        .update({
          status: 'signed',
          employee_signature: signatureData
        })
        .eq('id', selectedContractId);

      if (error) throw error;

      toast.success("Contrat signé avec succès");
      setIsSignatureDialogOpen(false);
      setSelectedContractId(null);
      setTermsAccepted(false);
      signaturePad.clear();
      refetch();
    } catch (error) {
      console.error('Error signing contract:', error);
      toast.error("Erreur lors de la signature du contrat");
    }
  };

  const openSignatureDialog = (contractId: string) => {
    setSelectedContractId(contractId);
    setTermsAccepted(false);
    setIsSignatureDialogOpen(true);
  };

  const handleDownloadPDF = (contract: Contract) => {
    const pdf = new jsPDF();
    
    // Add title
    pdf.setFontSize(16);
    pdf.text("Contrat de Travail", 20, 20);
    
    // Add employee name
    pdf.setFontSize(14);
    pdf.text(`Employé: ${contract.employee_name}`, 20, 40);
    
    // Add contract terms
    pdf.setFontSize(12);
    pdf.text("Termes du contrat:", 20, 60);
    
    const terms = [
      "Objet : Ce contrat précise les conditions relatives aux retenues sur salaire,",
      "aux écarts d'inventaire, aux différences de comptage, ainsi qu'aux conditions",
      "de résiliation.",
      "",
      "1. Retenues sur Salaire",
      "En cas de différence de comptage non justifiée de manière valable, l'employeur",
      "se réserve le droit d'effectuer une retenue équivalente à la somme manquante",
      "sur le salaire de l'employé.",
      "",
      "2. Écarts d'Inventaire",
      "Si un inventaire révèle des manquements de produits en stock sans justificatif",
      "valable, l'employé sera tenu pour responsable. Des sanctions pourront être",
      "appliquées, incluant des retenues sur salaire et des mesures disciplinaires.",
      "",
      "3. Différences de Comptage Répétées",
      "En cas d'erreurs répétées de comptage sans amélioration, malgré des",
      "avertissements, l'employeur se réserve le droit de procéder à des sanctions",
      "pouvant aller jusqu'au licenciement."
    ];

    let yPosition = 70;
    terms.forEach(line => {
      pdf.text(line, 20, yPosition);
      yPosition += 7;
    });

    // Add signature if present
    if (contract.employee_signature) {
      pdf.addImage(contract.employee_signature, 'PNG', 20, yPosition, 50, 30);
      yPosition += 35;
      pdf.text(`Signé le ${new Date(contract.updated_at).toLocaleDateString()}`, 20, yPosition);
    }

    // Download the PDF
    pdf.save(`contrat_${contract.employee_name.replace(/\s+/g, '_')}.pdf`);
    toast.success("Contrat téléchargé avec succès");
  };

  if (isLoading) {
    return (
      <DashboardLayout userType="owner">
        <div className="container mx-auto py-6">
          <div>Chargement...</div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout userType="owner">
      <div className="container mx-auto px-4 py-6 max-w-full overflow-x-hidden">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <PageTitle
            title="Contrat employé"
            subtitle="Consultez et signez vos contrats"
          />
          <Button 
            onClick={() => setIsAddDialogOpen(true)}
            className="w-full sm:w-auto flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Créer un contrat
          </Button>
        </div>

        {contracts && contracts.length > 0 ? (
          <div className="space-y-4">
            {contracts.map((contract) => (
              <Card key={contract.id} className="p-4 sm:p-6">
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">
                    Contrat de {contract.employee_name}
                  </h3>
                  
                  <ContractTerms contract={contract} />

                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                    <div className="text-sm text-gray-500">
                      Créé le {new Date(contract.contract_date).toLocaleDateString()}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {contract.status === 'pending' ? (
                        <Button 
                          className="w-full sm:w-auto"
                          onClick={() => openSignatureDialog(contract.id)}
                        >
                          Signer le contrat
                        </Button>
                      ) : (
                        <>
                          <div className="text-green-600 font-medium">
                            Contrat signé
                          </div>
                          <Button 
                            variant="outline"
                            onClick={() => handleDownloadPDF(contract)}
                            className="w-full sm:w-auto"
                          >
                            Télécharger PDF
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <div className="space-y-6">
            <Alert variant="default" className="border-orange-300 bg-orange-50">
              <AlertCircle className="h-5 w-5 text-orange-500" />
              <AlertTitle className="text-orange-800 mb-2">Importance du contrat employé</AlertTitle>
              <AlertDescription className="text-orange-700 space-y-4">
                <p>
                  Il est crucial de faire signer un contrat à votre employé avant qu&apos;il ne commence à travailler sur votre compte. Le contrat permet de :
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Protéger votre entreprise en cas de différences de comptage</li>
                  <li>Définir clairement les responsabilités de l&apos;employé</li>
                  <li>Établir les conditions de retenue sur salaire en cas d&apos;écart</li>
                  <li>Fixer les règles de gestion des stocks et de l&apos;inventaire</li>
                </ul>
                <div className="mt-4">
                  <Button 
                    onClick={() => setIsAddDialogOpen(true)}
                    className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600"
                  >
                    <FileSignature className="h-4 w-4" />
                    Créer votre premier contrat
                  </Button>
                </div>
              </AlertDescription>
            </Alert>
          </div>
        )}

        <AddContractDialog
          open={isAddDialogOpen}
          onOpenChange={setIsAddDialogOpen}
          onSuccess={() => {
            setIsAddDialogOpen(false);
            refetch();
          }}
        />

        <Dialog open={isSignatureDialogOpen} onOpenChange={setIsSignatureDialogOpen}>
          <DialogContent className="sm:max-w-[500px] w-[95vw] mx-auto">
            <DialogHeader>
              <DialogTitle>Signez le contrat</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="terms"
                  checked={termsAccepted}
                  onCheckedChange={(checked) => setTermsAccepted(checked as boolean)}
                />
                <label
                  htmlFor="terms"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  Lu et approuvé
                </label>
              </div>
              <div className="border rounded-md p-2 bg-white w-full">
                <SignatureCanvas
                  ref={(ref) => setSignaturePad(ref)}
                  canvasProps={{
                    className: "signature-canvas w-full h-[200px]",
                  }}
                />
              </div>
            </div>
            <DialogFooter className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => signaturePad?.clear()}
                className="w-full sm:w-auto"
              >
                Effacer
              </Button>
              <Button 
                onClick={handleSign}
                disabled={!termsAccepted}
                className="w-full sm:w-auto"
              >
                Valider la signature
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default EmployeeContract;
